/**
 * The test hook (frontend.md §8.3).
 *
 * `window.__ordago.getState()` lets a spec assert on store truth instead of
 * scraping the DOM — "is it my turn" is one field, not a selector plus a parse.
 *
 * Defined only in development or when a test asked for it. It exposes no more
 * than the client already holds, but a production page has no reason to hand a
 * console its own state, and the smaller the surface the less there is to keep
 * honest.
 */

import type { GameEvent } from '@ordago/rules';

import { heldRoom } from './net/useJoinRoom';
import { heldLobbyRoom } from './net/useLobby';
import { useStore } from './store';
import type { Slices } from './store/types';

declare global {
  interface Window {
    __ORDAGO_NO_ANIM?: boolean;
    __ordago?: {
      getState: () => Slices;
      /** The last join failure, which is otherwise only a rendered string. */
      joinError: () => string | null;
      /**
       * The raw Colyseus state, for comparing the mirror against the schema it
       * was projected from. `toJSON()` because a `Schema` is a live object with
       * accessors, not a plain value.
       */
      rawState: () => unknown;
      /** The same, for the lobby channel. */
      rawLobbyState: () => unknown;
      /** Every `game.event` this client was sent, in arrival order. */
      events: () => GameEvent[];
    };
  }
}

function toPlain(state: unknown): unknown {
  if (state === null || state === undefined) return null;
  const withJson = state as { toJSON?: () => unknown };
  return typeof withJson.toJSON === 'function' ? withJson.toJSON() : state;
}

/**
 * The event ledger, for tests only.
 *
 * It lives here rather than in the store because it is test state, not app
 * state — nothing renders from it, and a production page has no reason to
 * accumulate an unbounded list of everything that has ever happened.
 *
 * It exists because a gate that only asserts an outcome is weaker than it
 * looks: "the game reached 40" still passes if a whole lance silently stops
 * paying out, because the game just takes longer to get there. Keeping the
 * events lets a spec assert the *account* instead — that the final scoreboard
 * equals the sum of what this client was actually told.
 */
let ledger: GameEvent[] | null = null;

/**
 * Called for every inbound game event. A no-op — one null check — unless the
 * debug hook was installed, so production pays nothing for it.
 */
export function recordEvent(event: GameEvent): void {
  ledger?.push(event);
}

export function installDebugHook(): void {
  if (typeof window === 'undefined') return;
  if (!import.meta.env.DEV && window.__ORDAGO_NO_ANIM !== true) return;

  ledger = [];

  window.__ordago = {
    getState: () => useStore.getState(),
    joinError: () => useStore.getState().room.joinError,
    rawState: () => toPlain(heldRoom()?.state),
    rawLobbyState: () => toPlain(heldLobbyRoom()?.state),
    events: () => (ledger === null ? [] : [...ledger]),
  };
}
